// Systems / applications domain (step 6): the base app list plus admin-defined
// per-app overrides (hide/disable/edit) and custom apps. Reconstructed verbatim.

import { apps, type AppIntegration } from "./apps";
import { getAdminToken, getSetting, putSetting, getPublic } from "../lib/api";
import type { Lang } from "../lib/i18n";

export type SystemOverride = Partial<AppIntegration> & { id: string; hidden?: boolean; enabled?: boolean };
export interface SystemsConfig {
  overrides: Record<string, SystemOverride>;
  custom: AppIntegration[];
}

const SYSTEMS_CHANGED = "kd-systems-changed";
let config: SystemsConfig = { overrides: {}, custom: [] };
let loading: Promise<void> | null = null;

function emitChange() {
  try {
    window.dispatchEvent(new Event(SYSTEMS_CHANGED));
  } catch {
    /* ignore */
  }
}
export function subscribeSystems(cb: () => void): () => void {
  window.addEventListener(SYSTEMS_CHANGED, cb);
  return () => window.removeEventListener(SYSTEMS_CHANGED, cb);
}

/** Apply an override's editable fields onto a base app (hidden/enabled/id excluded). */
function applyOverride(base: AppIntegration, override?: SystemOverride): AppIntegration {
  if (!override) return base;
  const { hidden: _h, enabled: _e, id: _i, ...fields } = override;
  return { ...base, ...fields };
}

/** Visible systems for the wizard: base apps + custom, minus hidden/disabled. */
export function getVisibleSystems(): AppIntegration[] {
  const out: AppIntegration[] = [];
  for (const app of apps) {
    const o = config.overrides[app.id];
    if (!o?.hidden && o?.enabled !== false) out.push(applyOverride(app, o));
  }
  for (const custom of config.custom) {
    const o = config.overrides[custom.id];
    if (!o?.hidden && o?.enabled !== false) out.push(applyOverride(custom, o));
  }
  return out;
}

/** All systems with their admin state (for the admin panel). */
export function getAllSystemsAdmin() {
  const rows: Array<{ sys: AppIntegration; hidden: boolean; enabled: boolean; isCustom: boolean }> = [];
  for (const app of apps) {
    const o = config.overrides[app.id];
    rows.push({ sys: applyOverride(app, o), hidden: !!o?.hidden, enabled: o?.enabled !== false, isCustom: false });
  }
  for (const custom of config.custom) {
    const o = config.overrides[custom.id];
    rows.push({ sys: applyOverride(custom, o), hidden: !!o?.hidden, enabled: o?.enabled !== false, isCustom: true });
  }
  return rows;
}

export function loadSystems(): Promise<void> {
  return (
    loading ||
    (loading = (async () => {
      try {
        const res = getAdminToken() ? await getSetting<SystemsConfig>("systems") : await getPublicSystems();
        const value = res as any;
        if (value && typeof value === "object") {
          config = { overrides: value.overrides || {}, custom: Array.isArray(value.custom) ? value.custom : [] };
        }
        emitChange();
      } finally {
        loading = null;
      }
    })())
  );
}

async function getPublicSystems(): Promise<SystemsConfig | null> {
  const res = await getPublic<{ value?: SystemsConfig } | SystemsConfig>("systems");
  const data = res.data as any;
  return data && "value" in data ? data.value : data;
}

async function saveSystems(next: SystemsConfig) {
  config = next;
  emitChange();
  return putSetting("systems", next);
}

// --- Admin CRUD ---
function setOverride(id: string, patch: Partial<SystemOverride>) {
  return saveSystems({ ...config, overrides: { ...config.overrides, [id]: { ...config.overrides[id], ...patch, id } } });
}
export const setSystemHidden = (id: string, hidden: boolean) => setOverride(id, { hidden });
export const setSystemEnabled = (id: string, enabled: boolean) => setOverride(id, { enabled });
export function upsertCustomSystem(app: AppIntegration) {
  return saveSystems({ ...config, custom: [...config.custom.filter((c) => c.id !== app.id), app] });
}
export function deleteSystem(id: string) {
  const isBase = apps.some((a) => a.id === id);
  if (isBase) {
    return saveSystems({ ...config, overrides: { ...config.overrides, [id]: { ...config.overrides[id], id, hidden: true } } });
  }
  const { [id]: _removed, ...rest } = config.overrides;
  return saveSystems({ overrides: rest, custom: config.custom.filter((c) => c.id !== id) });
}
export function resetSystemOverride(id: string) {
  const { [id]: _removed, ...rest } = config.overrides;
  return saveSystems({ ...config, overrides: rest });
}
export function resetAllSystems() {
  return saveSystems({ overrides: {}, custom: [] });
}
/** Update editable fields of a system (built-in → override, custom → replace). */
export function updateSystem(id: string, fields: Partial<AppIntegration>) {
  const isCustom = config.custom.some((c) => c.id === id);
  if (isCustom) {
    return saveSystems({ ...config, custom: config.custom.map((c) => (c.id === id ? { ...c, ...fields, id } : c)) });
  }
  return setOverride(id, { ...fields, id });
}
export function exportSystems(): string {
  return JSON.stringify(config, null, 2);
}
export async function importSystems(json: string): Promise<boolean> {
  try {
    const parsed = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null) return false;
    await saveSystems({ overrides: parsed.overrides || {}, custom: Array.isArray(parsed.custom) ? parsed.custom : [] });
    return true;
  } catch {
    return false;
  }
}
export function newSystem(): AppIntegration {
  return {
    id: "", color: "#2d7a4a", bgColor: "#2d7a4a26", logo: "", nameAr: "", nameEn: "", catAr: "", catEn: "",
    descAr: "", descEn: "", usersAr: [], usersEn: [], accessAr: "", accessEn: "", url: "", hasApp: false, paths: ["all"], icon: "",
  };
}

// --- Localised field accessors ---
export const systemName = (a: AppIntegration, lang: Lang) => (lang === "ar" ? a.nameAr : a.nameEn);
export const systemCategory = (a: AppIntegration, lang: Lang) => (lang === "ar" ? a.catAr : a.catEn);
export const systemDesc = (a: AppIntegration, lang: Lang) => (lang === "ar" ? a.descAr : a.descEn);
export const systemAccess = (a: AppIntegration, lang: Lang) => (lang === "ar" ? a.accessAr : a.accessEn);
